import { ref } from "vue";
import jwtDecode from "jwt-decode";
import { apiRequest, isSuccess } from "./request";
import {
  setAuthToken,
  getAuthToken,
  removeAuthToken,
  setRefreshToken,
  getRefreshToken,
  removeRefreshToken,
} from "../utils";
import { AUTH } from "../utils/APIs";
import { ROUTES } from "../routes/names";
import { AUTHENTICATION_URL } from "../utils/constants";
import {
  AUTH_STORAGE_KEYS,
  AUTH_ROUTES,
  AUTH_FAILURE_REASONS,
  HTTP_STATUS,
} from "../constants/auth";
import { logoutFromAuth0 } from "./auth0";
import HttpClient from "project-blue-http-client";
import { envs } from "../utils";
import pbDomain from "../utils/pbDomain";
export const isSigningIn = ref(false);
export const LoginReqController = ref(new AbortController());
export const isValidatingToken = ref(false);
export const ValidateTokenReqController = ref(new AbortController());
export const isRefreshingToken = ref(false);
export const RefreshTokenReqController = ref(new AbortController());
export const isSigningOut = ref(false);
export const LogoutReqController = ref(new AbortController());
export const isResetingPassword = ref(false);
export const ResetPasswordReqController = ref(new AbortController());
export const isSigningUp = ref(false);
export const RegisterReqController = ref(new AbortController());
export const isSudoLoggingIn = ref(false);
export const SudoLoginReqController = ref(new AbortController());

export const register = async (credentials) => {
  RegisterReqController.value.abort();
  RegisterReqController.value = new AbortController();
  isSigningUp.value = true;
  const response = await apiRequest(
    AUTH.REGISTER,
    "POST",
    {
      fullname: credentials.name,
      email: credentials.email,
      password: credentials.password,
      company: credentials.company,
      country: credentials.country,
      contactNumber: credentials.contactNumber,
      interests: credentials.interests || [],
      sectors: credentials.sectors || [],
      marketingPromotions: true,
    },
    RegisterReqController.value.signal,
    false
  );
  isSigningUp.value = false;

  if (response?.status === HTTP_STATUS.CONFLICT) window.$message.error("Email already exists");
  else if (isSuccess(response?.status)) return true;

  return false;
};

export const login = async (credentials) => {
  LoginReqController.value.abort();
  LoginReqController.value = new AbortController();
  isSigningIn.value = true;

  let data = {
    email: credentials.email,
    password: credentials.password,
  };

  let isAuthenticated = false;

  let client = new HttpClient(AUTHENTICATION_URL);
  await client.post("authentication/authenticate", data).then(response => {
    isAuthenticated = (response?.permissions ?? null) != null || response.status == 1;
    isSigningIn.value = false;
    
    if (!isAuthenticated) {
      // Handle different failure scenarios based on response status
      if (response.status === 2) {
        // Account deactivated or sign-in failure
        window.location.href = `/#${AUTH_ROUTES.AUTH_FAILURE}?type=${AUTH_FAILURE_REASONS.DEACTIVATED}`;
      } else {
        window.$message.error("Invalid username and password combination");
      }
    }
  }).catch(error => {
    isSigningIn.value = false;
    // Handle different error scenarios
    if (error.status === HTTP_STATUS.NOT_FOUND) {
      // Account deactivated
      window.location.href = `/#${AUTH_ROUTES.AUTH_FAILURE}?type=${AUTH_FAILURE_REASONS.DEACTIVATED}`;
    } else if (error.status === HTTP_STATUS.INTERNAL_SERVER_ERROR) {
      // Sign-in failure
      window.location.href = `/#${AUTH_ROUTES.AUTH_FAILURE}?type=${AUTH_FAILURE_REASONS.SIGNIN_FAILURE}`;
    } else {
      window.$message.error("Invalid username and password combination");
    }
  });
  return isAuthenticated;
};

export const sudoLogin = async (credentials) => {
  SudoLoginReqController.value.abort();
  SudoLoginReqController.value = new AbortController();
  isSudoLoggingIn.value = true;

  let data = {
    email: credentials.email,
    tempExternalAuthUserId: credentials.auth0id,
    tempUserGuid: credentials.userGuid,
  };

  let isAuthenticated = false;

  let client = new HttpClient(AUTHENTICATION_URL);
  await client.post("authentication/ExternalAuthenticate", data).then(response => {
    isAuthenticated = (response?.permissions ?? null) != null || response.status == 1;
    isSudoLoggingIn.value = false;
    
    if (!isAuthenticated) {
      // Handle different failure scenarios based on response status
      if (response.status === 2) {
        // Account deactivated or sign-in failure
        window.location.href = `/#${AUTH_ROUTES.AUTH_FAILURE}?type=${AUTH_FAILURE_REASONS.DEACTIVATED}`;
      } else {
        window.$message.error("Invalid sudo login credentials");
      }
    }
  }).catch(error => {
    isSudoLoggingIn.value = false;
    // Handle different error scenarios
    if (error.status === HTTP_STATUS.NOT_FOUND) {
      // Account deactivated
      window.location.href = `/#${AUTH_ROUTES.AUTH_FAILURE}?type=${AUTH_FAILURE_REASONS.DEACTIVATED}`;
      localStorage.removeItem(AUTH_STORAGE_KEYS.USER_EMAIL);
    } else if (error.status === HTTP_STATUS.INTERNAL_SERVER_ERROR) {
      // Sign-in failure
      window.location.href = `/#${AUTH_ROUTES.AUTH_FAILURE}?type=${AUTH_FAILURE_REASONS.SIGNIN_FAILURE}`;
      localStorage.removeItem(AUTH_STORAGE_KEYS.USER_EMAIL);
    } else {
      window.$message.error("Invalid sudo login credentials");
      localStorage.removeItem(AUTH_STORAGE_KEYS.USER_EMAIL);
    }
  });
  return isAuthenticated;
};

export const getUserByAuth0Id = async (auth0Id, email) => {
  try {
    const client = new HttpClient(AUTHENTICATION_URL);
    const response = await client.get(
      `user/getUserByAuth0Id?auth0Id=${encodeURIComponent(auth0Id)}&email=${encodeURIComponent(email)}`
    );
    return response;
  } catch (error) {
    console.error("Failed to get or link user by Auth0 ID:", error);
    return null;
  }
};

const renewTokenOrLogout = async (router) => {
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.JWT);
  const isAuthorized = token !== "undefined" && Boolean(token);
  if (!isAuthorized) {
    await logout(router);
    if (router) {
      router.push({ name: ROUTES.LOGIN.name });
    }
  }
};

export const refreshToken = async () => {
  RefreshTokenReqController.value.abort();
  RefreshTokenReqController.value = new AbortController();
  isRefreshingToken.value = true;
  //pop up to re-auth
  isRefreshingToken.value = false;

  return false;
};

export const logout = async (router) => {
  localStorage.removeItem(AUTH_STORAGE_KEYS.USER_EMAIL);
  LogoutReqController.value.abort();
  LogoutReqController.value = new AbortController();
  isSigningOut.value = true;

  removeAuthToken();
  removeRefreshToken();

  isSigningOut.value = false;

  try {
    await logoutFromAuth0(pbDomain);
  } catch (e) {
    window.location.href = pbDomain;
  }
};

export const resetPassword = async (code, password) => {
  ResetPasswordReqController.value.abort();
  ResetPasswordReqController.value = new AbortController();
  isResetingPassword.value = true;

  let data = {
    resetPasswordCode: code,
    password: password
  };

  let baseUrl = envs("AUTHENTICATION");
  let client = new HttpClient(`${baseUrl}`);
  await client.post("/user/resetPassword", data)
    .then(() => {
      isResetingPassword.value = false;
    });
};
